import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { PopupService } from '../../../../shared/components/popup/popup.service';
import { RouterLink } from '@angular/router';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import {
  JOB_STATUSES_NEW_TAGS,
  JOB_STATUSES_ACTIVE,
  LANGUAGES,
} from '../../../../shared/config/constants';
import { VendorOrderSummary } from '../../../../shared/models/vendor-order.model';
import { VendorOrdersService } from './services/vendor-orders.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TargetLanguagesModalComponent } from './target-languages-modal/target-languages-modal.component';
import { TranslationManagerNavigationService } from '../../../../shared/services/translation-manager-navigation.service';

interface StatusItem {
  label: string;
  count: number;
}

export interface TranslationJobRow {
  jobId: string;
  jobName: string;
  requestDate: string;
  requestTimestamp: number;
  sourceLanguage: string;
  targetLanguages: string[];
  allTargetLanguages: string[];
  targetOverflowCount: number;
  targetDate: string;
  vendor: string;
  statusLabel: string;
  statusKey: string;
  statusIcon: string | null;
  rawJobStatus: string;
  files: VendorOrderSummary['files'];
  newMessage: boolean;
}

const STATUS_LABELS = Array.from(new Set(Object.values(JOB_STATUSES_NEW_TAGS)));

const LOCALE_LOOKUP = new Map<string, string>(Object.entries(LANGUAGES));

const STATUS_ICON_MAP: Readonly<Record<string, string>> = {
  'Quotes Pending': 'schedule',
  'Quotes to be Reviewed': 'request_quote',
  'In Progress': 'article_person',
  Cancelled: 'cancel',
  Completed: 'check_circle',
  Rejected: 'do_not_disturb_on',
  'Not Supported': 'warning',
};

@Component({
  selector: 'app-translation-manager',
  standalone: true,
  imports: [BreadcrumbComponent, MatIconModule, RouterLink],
  templateUrl: './translation-manager.component.html',
  styleUrl: './translation-manager.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TranslationManagerComponent implements OnInit {
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly popupService = inject(PopupService);
  private readonly translationManagerNavigationService = inject(
    TranslationManagerNavigationService,
  );
  readonly notificationService = inject(NotificationService);
  private readonly vendorOrders = signal<VendorOrderSummary[]>([]);
  private readonly cameFromDetail = history.state?.fromDetail === true;
  private readonly cameFromBreadcrumb = history.state?.fromBreadcrumb === true;
  private readonly incomingNotificationSnapshot =
    typeof history.state?.notificationSnapshot === 'string'
      ? history.state.notificationSnapshot
      : '';
  private readonly incomingFilters = this.getIncomingFilters();
  readonly sortDescending = signal(true);
  readonly sortColumn = signal<
    'requestDate' | 'targetDate' | 'vendor' | 'status'
  >('requestDate');
  readonly sortAscending = signal(false);
  readonly activeFilters = signal<Set<string>>(new Set());

  readonly isLoading = signal(true);
  readonly errorMessage = signal<string | null>(null);

  private readonly selectedFiltersHistorySync = effect(() => {
    this.updateHistorySelectedFilters(this.activeFilterList());
  });

  readonly activeFilterList = computed(() => [...this.activeFilters()]);

  private readonly allRows = computed<TranslationJobRow[]>(() => {
    const direction = this.sortDescending() ? -1 : 1;

    return this.vendorOrders()
      .filter((order) => this.shouldDisplayOrder(order.jobStatus))
      .map((order) => this.mapOrderToRow(order))
      .sort((first, second) => {
        const col = this.sortColumn();
        const asc = this.sortAscending();
        const dir = asc ? 1 : -1;

        switch (col) {
          case 'vendor': {
            const cmp = first.vendor.localeCompare(second.vendor);
            return cmp === 0
              ? (first.requestTimestamp - second.requestTimestamp) * direction
              : cmp * dir;
          }
          case 'targetDate': {
            const a = new Date(first.targetDate).getTime() || 0;
            const b = new Date(second.targetDate).getTime() || 0;
            return a === b
              ? (first.requestTimestamp - second.requestTimestamp) * direction
              : (a - b) * dir;
          }
          case 'status': {
            const cmp = first.statusLabel.localeCompare(second.statusLabel);
            return cmp === 0
              ? (first.requestTimestamp - second.requestTimestamp) * direction
              : cmp * dir;
          }
          default:
            return (
              (first.requestTimestamp - second.requestTimestamp) * direction
            );
        }
      });
  });

  private shouldDisplayOrder(jobStatus?: string): boolean {
    if (!jobStatus) {
      return true;
    }

    const normalized = jobStatus.toUpperCase();
    // Keep cancelled jobs visible for auditability while filtering out hard-deleted rows.
    return normalized !== 'DELETED';
  }

  readonly rows = computed<TranslationJobRow[]>(() => {
    const all = this.allRows();
    const filters = this.activeFilters();

    if (filters.size === 0) {
      return all;
    }

    return all.filter((row) => filters.has(row.statusLabel));
  });

  readonly statusItems = computed<StatusItem[]>(() => {
    const allRows = this.allRows();

    return STATUS_LABELS.map((label) => ({
      label,
      count: allRows.filter((row) => row.statusLabel === label).length,
    }));
  });

  private readonly newMessageStatusLabels = computed<Set<string>>(() => {
    const labels = new Set<string>();
    for (const row of this.allRows()) {
      if (row.newMessage) {
        labels.add(row.statusLabel);
      }
    }
    return labels;
  });

  ngOnInit(): void {
    this.translationManagerNavigationService.clearFilters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.translationManagerNavigationService.consumePendingClearFiltersRequest();
        this.resetFiltersAndSorting();
      });

    const hasPendingClearRequest =
      this.translationManagerNavigationService.consumePendingClearFiltersRequest();

    if (hasPendingClearRequest) {
      this.resetFiltersAndSorting();
    } else if (this.incomingFilters.length > 0) {
      this.activeFilters.set(new Set(this.incomingFilters));
    }

    this.loadOrders(this.shouldForceRefreshOnInit());
  }

  private resetFiltersAndSorting(): void {
    this.activeFilters.set(new Set());
    this.sortColumn.set('requestDate');
    this.sortDescending.set(true);
    this.sortAscending.set(false);
  }

  private shouldForceRefreshOnInit(): boolean {
    const cameFromReturnNavigation =
      this.cameFromDetail || this.cameFromBreadcrumb;

    if (!cameFromReturnNavigation) {
      return true;
    }

    if (!this.incomingNotificationSnapshot) {
      return false;
    }

    const snapshotOnEntry = this.parseNotificationSnapshot(
      this.incomingNotificationSnapshot,
    );
    const currentSnapshot = this.parseNotificationSnapshot(
      this.notificationService.notificationSnapshotKey(),
    );

    for (const key of currentSnapshot) {
      if (!snapshotOnEntry.has(key)) {
        return true;
      }
    }

    return false;
  }

  private parseNotificationSnapshot(snapshot: string): Set<string> {
    return new Set(
      snapshot
        .split('|')
        .map((item) => item.trim())
        .filter((item) => item.length > 0),
    );
  }

  toggleRequestDateSort(): void {
    if (this.sortColumn() === 'requestDate') {
      this.sortDescending.update((value) => !value);
    } else {
      this.sortColumn.set('requestDate');
      this.sortDescending.set(true);
    }
  }

  toggleVendorSort(): void {
    this.toggleColumnSort('vendor');
  }

  toggleTargetDateSort(): void {
    this.toggleColumnSort('targetDate');
  }

  toggleStatusSort(): void {
    this.toggleColumnSort('status');
  }

  private toggleColumnSort(col: 'vendor' | 'targetDate' | 'status'): void {
    if (this.sortColumn() === col) {
      this.sortAscending.update((v) => !v);
    } else {
      this.sortColumn.set(col);
      this.sortAscending.set(true);
    }
  }

  toggleFilter(label: string): void {
    this.activeFilters.update((current) => {
      const next = new Set(current);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }

  removeFilter(label: string): void {
    this.activeFilters.update((current) => {
      const next = new Set(current);
      next.delete(label);
      return next;
    });
  }

  isFilterActive(label: string): boolean {
    return this.activeFilters().has(label);
  }

  hasStatusNotification(label: string): boolean {
    return (
      this.notificationService.notifiedStatusLabels().has(label) ||
      this.newMessageStatusLabels().has(label)
    );
  }

  hasRowNotification(row: TranslationJobRow): boolean {
    if (!row.jobId || row.jobId === '--') {
      return false;
    }

    return (
      this.notificationService.isAwaitingVendorJob(row.jobId) ||
      this.notificationService.isCompletedDocsJob(row.jobId) ||
      row.newMessage
    );
  }

  private loadOrders(forceRefresh = false): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.vendorOrdersService
      .getVendorOrders(false, forceRefresh)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orders) => {
          this.vendorOrders.set(orders ?? []);
          this.isLoading.set(false);
        },
        error: () => {
          this.vendorOrders.set([]);
          this.isLoading.set(false);
          this.errorMessage.set(
            'Unable to load translation jobs, please refresh the page or try again later.',
          );
        },
      });
  }

  private mapOrderToRow(order: VendorOrderSummary): TranslationJobRow {
    const targetLanguages = this.getTargetLanguages(order.targetLocaleId);
    const visibleTargets = targetLanguages.slice(0, 2);
    const allTargetLanguages = [...targetLanguages].sort((a, b) =>
      a.localeCompare(b),
    );

    const statusLabel = this.getStatusLabel(order.jobStatus);

    return {
      jobId: order.jobUid ?? '--',
      jobName: order.customJobName ?? '--',
      requestDate: this.formatDateValue(order.createdAt),
      requestTimestamp: order.createdAt ?? 0,
      sourceLanguage: this.getLocaleName(order.sourceLocaleId),
      targetLanguages: visibleTargets,
      allTargetLanguages,
      targetOverflowCount: Math.max(0, targetLanguages.length - 2),
      targetDate: this.formatDateValue(this.getTargetDate(order)),
      vendor: this.getVendorName(order.estimates),
      statusLabel,
      statusKey: this.getStatusKey(statusLabel),
      statusIcon: this.getStatusIcon(statusLabel),
      rawJobStatus: order.jobStatus ?? '',
      files: order.files,
      newMessage: order.newMessage === true,
    };
  }

  openLanguagesModal(row: TranslationJobRow): void {
    this.popupService.open({
      title: `Translation ${row.jobId}`,
      hideButtons: true,
      disableClose: true,
      component: TargetLanguagesModalComponent,
      componentInputs: {
        jobId: row.jobId,
        jobName: row.jobName,
        sourceLanguage: row.sourceLanguage,
        allTargetLanguages: row.allTargetLanguages,
      },
    });
  }

  private getTargetDate(order: VendorOrderSummary): string | undefined {
    const estimatedDate = order.estimatedDeliveryDate;
    const fixedDate = order.fixedDeliveryDate;
    let targetDate = order.deadline;

    if (estimatedDate) {
      targetDate = estimatedDate;
    }

    if (fixedDate && fixedDate !== estimatedDate) {
      targetDate = fixedDate;
    }

    return targetDate;
  }

  private formatDateValue(value?: number | string): string {
    if (value === undefined || value === null) {
      return '--';
    }

    const dateValue = new Date(value);
    if (Number.isNaN(dateValue.getTime())) {
      return '--';
    }

    return formatDate(dateValue, 'dd/MMM/yyyy', 'en-GB');
  }

  private getLocaleName(localeId?: string): string {
    if (!localeId) {
      return '--';
    }

    return LOCALE_LOOKUP.get(localeId) ?? localeId;
  }

  private getTargetLanguages(localeIds?: string[]): string[] {
    if (!localeIds || localeIds.length === 0) {
      return [];
    }

    return localeIds.map((localeId) => this.getLocaleName(localeId));
  }

  private getVendorName(estimates?: VendorOrderSummary['estimates']): string {
    if (!estimates || estimates.length === 0) {
      return '--';
    }

    const acceptedEstimate = estimates.find(
      (estimate) =>
        !!estimate.jobStatus &&
        JOB_STATUSES_ACTIVE.includes(
          estimate.jobStatus as (typeof JOB_STATUSES_ACTIVE)[number],
        ),
    );

    return acceptedEstimate?.name ?? '--';
  }

  private getStatusLabel(status?: string): string {
    if (!status) {
      return '--';
    }

    const mappedStatus =
      JOB_STATUSES_NEW_TAGS[status as keyof typeof JOB_STATUSES_NEW_TAGS];
    if (mappedStatus) {
      return mappedStatus;
    }

    return this.formatFallbackStatus(status);
  }

  private formatFallbackStatus(status: string): string {
    return status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  private getStatusKey(statusLabel?: string): string {
    if (!statusLabel || statusLabel === '--') {
      return 'unknown';
    }

    return statusLabel
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  private getStatusIcon(statusLabel?: string): string | null {
    if (!statusLabel || statusLabel === '--') {
      return null;
    }

    return STATUS_ICON_MAP[statusLabel] ?? null;
  }

  private updateHistorySelectedFilters(filters: string[]): void {
    const nextState = { ...(history.state ?? {}) } as {
      selectedFilters?: string[];
    };

    if (filters.length > 0) {
      nextState.selectedFilters = [...filters];
    } else {
      delete nextState.selectedFilters;
    }

    history.replaceState(nextState, '');
  }

  private getIncomingFilters(): string[] {
    const state = history.state as { selectedFilters?: string[] } | null;
    if (!Array.isArray(state?.selectedFilters)) {
      return [];
    }

    return state.selectedFilters.filter(
      (value) => typeof value === 'string' && value.trim().length > 0,
    );
  }
}
